import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import { useAuth } from '../../Common/Configurations/AuthContext';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import AddCustomerModal from '../Master/AddCustomerModal';

// Import all template components
import PremiumModernTemplate from "./PackagePdfTemplates/PremiumModernTemplate";
import SimpleEleganceTemplate from "./PackagePdfTemplates/SimpleEleganceTemplate";
import ModernCorporateTemplate from "./PackagePdfTemplates/ModernCorporateTemplate";
import LuxuryMagazineTemplate from "./PackagePdfTemplates/LuxuryMagazineTemplate";
import VibrantAdventureTemplate from "./PackagePdfTemplates/VibrantAdventureTemplate";
import ClassicEleganceTemplate from "./PackagePdfTemplates/ClassicEleganceTemplate";
import GeoExplorerTemplate from "./PackagePdfTemplates/GeoExplorerTemplate";
import ArtisanJournalTemplate from "./PackagePdfTemplates/ArtisanJournalTemplate";


const templateComponentMap = {
    "premium-modern": PremiumModernTemplate,
    "simple-elegance": SimpleEleganceTemplate,
    "modern-corporate": ModernCorporateTemplate,
    "luxury-magazine": LuxuryMagazineTemplate,
    "vibrant-adventure": VibrantAdventureTemplate,
    "classic-elegance": ClassicEleganceTemplate,
    "geo-explorer": GeoExplorerTemplate,
    "artisan-journal": ArtisanJournalTemplate,

};

const PackageShareModal = ({ show, onHide, packageData, initialTemplateId }) => {
    const { user, refreshUserProfile } = useAuth();
    const pdfTemplateRef = useRef(null);

    const [activeTab, setActiveTab] = useState('customer');
    const [isSending, setIsSending] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [campaignMessage, setCampaignMessage] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('premium-modern');
    
    // State for Customers
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');

    // State for Agents
    const [agents, setAgents] = useState([]);
    const [selectedAgentIds, setSelectedAgentIds] = useState([]);
    const [agentSearchTerm, setAgentSearchTerm] = useState('');
    
    const [initialLoadDone, setInitialLoadDone] = useState({ customer: false, agent: false });
     const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);


     const templates = [
        { id: "minimal-professional", name: "Minimal Professional" },
        { id: "simple-elegance", name: "Simple Elegance" },
        { id: "classic-elegance", name: "Classic Elegance" },
        { id: "nordic-minimalist", name: "Nordic Minimalist" },
        { id: "modern-corporate", name: "Modern Corporate" },
        { id: "travel-magazine", name: "Travel Magazine" },
        { id: "vibrant-adventure", name: "Vibrant Adventure" },
        { id: "geo-explorer", name: "Geo Explorer" },
        { id: "premium-modern", name: "Premium Modern" },
        { id: "luxury-magazine", name: "Luxury Magazine" },
        { id: "artisan-journal", name: "Artisan Journal" },
        { id: "european-grandeur", name: "European Grandeur" },
        { id: "premium-luxury", name: "Premium Luxury" },
    ];
    // Effect to reset state when the modal opens
    useEffect(() => {
        if (show) {
            setSelectedTemplate(initialTemplateId || 'premium-modern');
            setActiveTab('customer');
            setCustomers([]);
            setSelectedCustomerIds([]);
            setCustomerSearchTerm('');
            setAgents([]);
            setSelectedAgentIds([]);
            setAgentSearchTerm('');
            setInitialLoadDone({ customer: false, agent: false });
        }
    }, [show, initialTemplateId]);

    // Effect to set the campaign message
    useEffect(() => {
        if (packageData?.package) {
            const pkg = packageData.package;
            setCampaignMessage(`Exciting offer! Check out our "${pkg.packageName}" package.`);
        }
    }, [packageData]);

    // Data Fetching and Initial Selection Logic
     const GetCustomers = async () => {
        if (!user?.cid) return;
        setIsLoadingData(true);
        try {
            let url = `${config.GETALLCUSTOMERSBYCOMPANYID}/${user.cid}`;
            if (customerSearchTerm) url += `?search=${encodeURIComponent(customerSearchTerm)}`;
            const customerList = await apiService.getSingle(url);
            setCustomers(customerList || []);

            // Only auto-select all on the very first load without search
            if (!customerSearchTerm && !initialLoadDone.customer) {
                setSelectedCustomerIds((customerList || []).map(c => c.customerId));
                setInitialLoadDone(prev => ({ ...prev, customer: true }));
            }
        } catch (error) {
            console.error(`Failed to fetch customers:`, error);
            toast.error(`Could not load customer list.`);
        } finally {
            setIsLoadingData(false);
        }
    };
    
    const GetAgents = async () => {
        if (!user?.cid) return;
        setIsLoadingData(true);
        try {
            let url = `${config.GETALLAGENTBYCOMPANY}/${user.cid}`;
            if (agentSearchTerm) url += `?search=${encodeURIComponent(agentSearchTerm)}`;
            const agentList = await apiService.getSingle(url);
            setAgents(agentList || []);

            if (!agentSearchTerm && !initialLoadDone.agent) {
                setSelectedAgentIds((agentList || []).map(a => a.companyId));
                setInitialLoadDone(prev => ({ ...prev, agent: true }));
            }
        } catch (error) {
            console.error(`Failed to fetch agents:`, error);
            toast.error(`Could not load agent list.`);
        } finally {
            setIsLoadingData(false);
        }
    };
    
    // Updated useEffect to use the new fetch functions
    useEffect(() => {
        if (!show) return;
        
        const getData = () => {
            if (activeTab === 'customer') {
                GetCustomers();
            } else if (activeTab === 'agent') {
                GetAgents();
            }
        };

        const debounceTimer = setTimeout(getData, 300);
        return () => clearTimeout(debounceTimer);

    }, [show, customerSearchTerm, agentSearchTerm, activeTab]);

    // --- NEW HANDLER for when a customer is successfully added ---
    const handleCustomerAdded = () => {
        // Hide the add modal
        setShowAddCustomerModal(false);
        // Refresh the customer list in the main modal
        GetCustomers();
    };
    
    const handleSelection = (id, isChecked) => {
        if (activeTab === 'customer') {
            setSelectedCustomerIds(prev => isChecked ? [...prev, id] : prev.filter(selectedId => selectedId !== id));
        } else {
            setSelectedAgentIds(prev => isChecked ? [...prev, id] : prev.filter(selectedId => selectedId !== id));
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        if (activeTab === 'customer') {
            const visibleIds = customers.map(c => c.customerId);
            setSelectedCustomerIds(isChecked ? visibleIds : []);
        } else {
            const visibleIds = agents.map(a => a.companyId);
            setSelectedAgentIds(isChecked ? visibleIds : []);
        }
    };

    // --- STEP 1: Create a dedicated tab change handler ---
    const handleTabChange = (tab) => {
        if (activeTab === tab) return; // Do nothing if clicking the same tab

        // Immediately show loading spinner to prevent flash of old content
        setIsLoadingData(true); 
        setActiveTab(tab);
    };
    
   
    const renderTemplateForPdf = () => {
        if (!packageData) return null;
        const TemplateComponent = templateComponentMap[selectedTemplate];
        if (!TemplateComponent) return <PremiumModernTemplate packageData={packageData} user={user} />;
        return <TemplateComponent packageData={packageData} user={user} />;
    };
    
    const handleSendPackage = async () => {
        const selectedCount = activeTab === 'customer' ? selectedCustomerIds.length : selectedAgentIds.length;
        if (selectedCount === 0) {
            toast.warn(`Please select at least one ${activeTab}.`);
            return;
        }
        if (!campaignMessage) {
            toast.warn("Please enter a message for the campaign.");
            return;
        }
        setIsSending(true);
        try {
            const input = pdfTemplateRef.current;
            if (!input) throw new Error("PDF template could not be found.");
            const canvas = await html2canvas(input, { scale: 1, useCORS: true });
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const ratio = canvas.height / canvas.width;
            const imgHeight = pdfWidth * ratio;
            let position = 0;
            let heightLeft = imgHeight;
            pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, position, pdfWidth, imgHeight);
            heightLeft -= pdf.internal.pageSize.getHeight();
            while (heightLeft > 0) {
                position -= pdf.internal.pageSize.getHeight();
                pdf.addPage();
                pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, position, pdfWidth, imgHeight);
                heightLeft -= pdf.internal.pageSize.getHeight();
            }
            const pdfBlob = pdf.output("blob");
            const pdfFile = new File([pdfBlob], `${packageData.package.packageName}.pdf`, { type: "application/pdf" });
            
            let recipientPhones;
            if (activeTab === 'customer') {
                recipientPhones = customers.filter(c => selectedCustomerIds.includes(c.customerId)).map(c => c.phoneNumber);
            } else {
                recipientPhones = agents.filter(a => selectedAgentIds.includes(a.companyId)).map(a => a.phoneNumber);
            }
            
            const formattedPhones = recipientPhones.filter(phone => phone).map(phone => phone.startsWith("91") ? phone : "91" + phone);

            if (formattedPhones.length === 0) {
                toast.error(`None of the selected ${activeTab}s have a valid phone number.`);
                setIsSending(false); return;
            }

            const formData = new FormData();
            formData.append("CompanyId", user.cid);
            formData.append("Caption", campaignMessage);
            formData.append("PdfFile", pdfFile);
            formattedPhones.forEach(phone => formData.append("CustomerPhones", phone));
            await apiService.postWithFile(config.SENDBULKPDF, formData);
            await refreshUserProfile();
            onHide();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to send WhatsApp message. Please try again.";
            toast.error(errorMessage);
        } finally {
            setIsSending(false);
        }
    };
    
    // Computed values for the UI (unchanged)
    const isAllVisibleSelected = activeTab === 'customer' 
        ? customers.length > 0 && customers.every(c => selectedCustomerIds.includes(c.customerId))
        : agents.length > 0 && agents.every(a => selectedAgentIds.includes(a.companyId));
    
    const selectedCount = activeTab === 'customer' ? selectedCustomerIds.length : selectedAgentIds.length;
    const currentList = activeTab === 'customer' ? customers : agents;
    const currentSearchTerm = activeTab === 'customer' ? customerSearchTerm : agentSearchTerm;
    const setCurrentSearchTerm = activeTab === 'customer' ? setCustomerSearchTerm : setAgentSearchTerm;
    
    return (
        <>
            {packageData && (<div style={{ position: "absolute", left: "-9999px", width: "210mm" }}><div ref={pdfTemplateRef}>{renderTemplateForPdf()}</div></div>)}
            <Modal show={show} onHide={onHide} onExited={() => setIsSending(false)} size="xl" centered>
                 <Modal.Header closeButton>
                    <Modal.Title>Share "{packageData?.package?.packageName}"</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-7">
                            {/* --- STEP 2: Use the new handler in the UI --- */}
                            <ul className="nav nav-tabs nav-tabs-custom nav-success mb-3" role="tablist">
                                <li className="nav-item"><a className={`nav-link ${activeTab === 'customer' ? 'active' : ''}`} href="#!" onClick={() => handleTabChange('customer')}>Customers</a></li>
                                <li className="nav-item"><a className={`nav-link ${activeTab === 'agent' ? 'active' : ''}`} href="#!" onClick={() => handleTabChange('agent')}>Agents</a></li>
                            </ul>
                            <h6>Select {activeTab === 'customer' ? 'Customers' : 'Agents'} ({selectedCount} Selected)</h6>
                            <div className="d-flex gap-2 mb-3">
                                <input type="text" className="form-control" placeholder={`Search ${activeTab}s by name or phone...`} value={currentSearchTerm} onChange={(e) => setCurrentSearchTerm(e.target.value)} />
                                {activeTab === 'customer' && (
                                    <Button variant="primary" onClick={() => setShowAddCustomerModal(true)}>
                                        Add
                                    </Button>
                                )}
                            </div>
                            <div className="d-flex align-items-center mb-2">
                                <input className="form-check-input me-2" type="checkbox" id="selectAllRecipients" onChange={handleSelectAll} checked={isAllVisibleSelected} />
                                <label className="form-check-label fw-semibold" htmlFor="selectAllRecipients">Select All</label>
                            </div>

                            <div className="table-responsive border rounded" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                {isLoadingData ? ( <div className="text-center p-5">Loading {activeTab}s...</div> ) : 
                                (currentList.length === 0 ? <div className="text-center p-5 text-muted">No {activeTab}s found.</div> : (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light" style={{ position: 'sticky', top: 0 }}><tr><th style={{ width: '50px' }}></th><th>Name</th><th>Contact</th></tr></thead>
                                        <tbody>
                                            {currentList.map(item => {
                                                const id = activeTab === 'customer' ? item.customerId : item.companyId;
                                                const name = activeTab === 'customer' ? item.customerName : item.companyName;
                                                const isSelected = activeTab === 'customer' ? selectedCustomerIds.includes(id) : selectedAgentIds.includes(id);

                                                return (
                                                    <tr key={id}>
                                                        <td><div className="form-check"><input className="form-check-input" type="checkbox" checked={isSelected} onChange={e => handleSelection(id, e.target.checked)} /></div></td>
                                                        <td>{name}</td>
                                                        <td>{item.phoneNumber}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                ))}
                            </div>
                        </div>
                        <div className="col-md-5">
                            <h6>Compose Message</h6>
                            <div className="mb-3">
                                <label className="form-label">Select PDF Template</label>
                                <select className="form-select" value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} >
                                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                 </select>                            </div>
                            <div className="mb-3">
                                <label className="form-label">WhatsApp Message</label>
                                <textarea className="form-control" rows="5" value={campaignMessage} onChange={(e) => setCampaignMessage(e.target.value)} placeholder="This message will be sent as a caption with the PDF."></textarea>
                            </div>
                            <h6>WhatsApp Preview</h6>
                            <div className="whatsapp-preview-box border rounded p-3" style={{backgroundColor: '#e5ddd5'}}>
                                <div className="bg-white rounded p-2">
                                    <div className="pdf-preview-icon text-center bg-light p-3 mb-2"><i className="ri-file-pdf-2-line" style={{fontSize: '48px', color: '#dc3545'}}></i><p className="mb-0 small text-muted">{packageData?.package?.packageName}.pdf</p></div>
                                    <div className="caption"><p className="mb-0 small">{campaignMessage || "Your message here..."}</p></div>
                                </div>
                            </div>
                        </div>
                     </div>
                </Modal.Body>
                <Modal.Footer>
                     <Button variant="secondary" onClick={onHide}>Cancel</Button>
                     <Button variant="success" onClick={handleSendPackage} disabled={isSending || isLoadingData || selectedCount === 0}>
                         <i className="ri-whatsapp-line me-1"></i>
                         {isSending ? 'Sending...' : `Send to ${selectedCount} Recipients`}                     </Button>
                </Modal.Footer>
            </Modal>
             <AddCustomerModal 
                show={showAddCustomerModal}
                onHide={() => setShowAddCustomerModal(false)}
                onCustomerAdded={handleCustomerAdded}
            />
         </>
     );
 };
export default PackageShareModal;